import { createContext } from 'react';

import type { IApiClientAuthorization } from '~/lib/api';
import type { IAppRegion } from '~/lib/appSettings';
import type { IApiGetUserResponseBody } from '../api/modules/Users';
import type {
  AuthClient,
  IAuthAsUser,
  IAuthCredentials,
  IAuthLogOutOptions,
  IAuthNavigateToLogInOptions,
} from './AuthClient';
import type { IAuthState } from './AuthState';
import type { IAuthCacheEntry } from './cache';
import { initialAuthState } from './AuthState';

/**
 * Contains the authenticated state and authentication methods provided by the
 * `useAuth` hook.
 */
export type IAuthContextValue = IAuthState & {
  navigateToLogIn: (options?: IAuthNavigateToLogInOptions) => Promise<void>;
  logInWithCredentials: (
    region: IAppRegion,
    credentials: IAuthCredentials,
  ) => Promise<IApiClientAuthorization>;
  logInAs: (
    region: IAppRegion,
    asUser: IAuthAsUser,
  ) => Promise<IApiClientAuthorization>;
  logOut: (options?: IAuthLogOutOptions) => Promise<void>;
  setAuthorization: (authorization: IApiClientAuthorization) => Promise<void>;
  clearCredentials: (sessionId?: string) => Promise<void>;
  getClient: () => AuthClient;
  switchSession: (
    sessionId: string,
  ) => Promise<IAuthCacheEntry<IApiGetUserResponseBody> | undefined>;
};

const stub = (): never => {
  throw new Error('You forgot to wrap your component in <AuthProvider />.');
};

export const initialContext: IAuthContextValue = {
  ...initialAuthState,
  navigateToLogIn: stub,
  logInWithCredentials: stub,
  logInAs: stub,
  logOut: stub,
  setAuthorization: stub,
  clearCredentials: stub,
  getClient: stub,
  switchSession: stub,
};

/**
 * The Auth Context.
 */
export const AuthContext = createContext<IAuthContextValue>(initialContext);
